import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSnack } from "../../providers/SnackBarProvider";
import ROUTES from "../../router/routesModel";
import {
  intendedDestination,
  saveIntendedDestination,
  takeIntendedDestination,
} from "../../router/intendedDestination";
import normalizeUser from "../helpers/normalization/normalizeUser";
import { useUser } from "../providers/UserProvider";
import { clearDrafts } from "../../native/drafts";

import {
  removeToken,
  setTokenInLocalStorage,
  getUserFromLocalStorage,
} from "../service/localStorage";
import {
  login,
  signup,
  syncSupabaseToApp,
  GetUser,
  GetAllUsers,
  deleteMyAccount,
} from "../service/userApi";
import { supabase, isSupabaseConfigured } from "../../lib/supabaseClient";
import { trackEvent } from "../../analytics/analytics";
import {
  startGoogleOAuthTrace,
  appendAuthStep,
  probeAuthStorage,
  mergeAuthTrace,
  sanitizeExchangeError,
} from "../../lib/authTrace";
import {
  isNativePlatform,
  buildAuthRedirectUrl,
  openExternalAuthUrl,
} from "../../lib/nativeAuth";

/** Shown when Supabase password login fails but legacy Mongo login is intentionally disabled. */
const SUPABASE_LOGIN_FAILURE_HINT =
  "This app uses Supabase Auth for beta. Mongo-only accounts must be migrated or invited in Supabase before login.";

/** Last resort when a failed sign-in carried no message of its own. */
const GENERIC_LOGIN_FAILURE = "Login failed — please try again";

/**
 * True when a sign-in failure could actually BE the un-migrated-account case,
 * and so is the only failure SUPABASE_LOGIN_FAILURE_HINT may be attached to (#41).
 *
 * The hint used to be attached on CONFIGURATION rather than on the reason, and
 * Supabase is configured in every shipping build — so a tester who lost signal,
 * got throttled, or had not confirmed their email was told their account needed
 * migrating out of Mongo.
 *
 * This deliberately enumerates the ONE case the hint is about rather than the
 * cases it is wrong about. The first draft of this fix did the opposite — it
 * listed "unrelated" messages and let everything else keep the hint — and that
 * shape fails OPEN: every message the list does not anticipate leaks the notice.
 * A skeptic broke it in one step with strings this app really produces, because
 * the text here is the raw PLATFORM message. `@supabase/auth-js`'s
 * `AuthRetryableFetchError` does not author one: `lib/fetch.js` rethrows
 * `_getErrorMessage(e)`, which returns `e.message` unchanged, and
 * `handleLogin` then does `throw error.message`. So "Failed to fetch" is
 * Chrome's wording only — Safari and the iOS WKWebView say "Load failed",
 * undici says "fetch failed", an auth-server 5xx arrives as its statusText
 * ("Service Unavailable"), and an aborted request says "The user aborted a
 * request." Safari and iOS Safari are both in this app's browserslist, so that
 * was defect #41 still live on a shipping platform.
 *
 * Enumerating the hint's own case instead fails CLOSED: an unrecognised failure
 * shows the platform's own words and no internal vocabulary, which is the
 * outcome this entry is about.
 *
 * "Invalid login credentials" is what Supabase returns BOTH for a Mongo-only
 * account that was never invited AND for a mistyped password. Those two are
 * indistinguishable from the client, so this case keeps the hint rather than
 * being guessed at either way — whether it should is a copy decision for the
 * owner, not one to make here.
 */
const mayBeUnmigratedAccount = (detail) =>
  String(detail || "")
    .toLowerCase()
    .includes("invalid login credentials");

const useHandleUsers = (currentQuery) => {
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [usersData, setUsersData] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState(null);
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");

  const { user, setUser, setToken } = useUser();
  // Web keeps the origin it always used; native gets the custom scheme the
  // manifest claims, because https://localhost is not reachable from the
  // external browser that finishes the flow (#83).
  const authCallbackUrl = buildAuthRedirectUrl(ROUTES.AUTH_CALLBACK);
  const navigate = useNavigate();
  const location = useLocation();
  const snack = useSnack();
  const requestStatus = useCallback(
    (loading, errorMessage, userData, updatedUser) => {
      setLoading(loading);
      setError(errorMessage);
      setUsersData(userData);
      if (updatedUser !== undefined) {
        setUser(updatedUser);
      }
    },
    [setUser]
  );

  useEffect(() => {
    setQuery(currentQuery);
  }, [currentQuery]);

  // Results arrive already matched by the server, so there is nothing to filter
  // here. Filtering in the browser is what forced the endpoint to return every
  // active user, and therefore what made it impossible to bound safely.
  useEffect(() => {
    setFilteredUsers(query ? allUsers : []);
  }, [allUsers, query]);

  const handleLogin = useCallback(
    async (userCredentials) => {
      try {
        setLoading(true);
        if (isSupabaseConfigured() && supabase) {
          const { data, error } = await supabase.auth.signInWithPassword({
            email: userCredentials.email,
            password: userCredentials.password,
          });
          if (error) throw error.message;
          const appToken = await syncSupabaseToApp(data.session.access_token);
          setTokenInLocalStorage(appToken);
          setToken(appToken);
          const userFromLocalStorage = getUserFromLocalStorage();
          requestStatus(false, null, null, userFromLocalStorage);
          snack("success", "Logged in successfully!");
          const from = intendedDestination(location, ROUTES.ROOT);
          navigate(from, { replace: true });
          return;
        }
        const token = await login(userCredentials);
        setTokenInLocalStorage(token);
        setToken(token);
        const userFromLocalStorage = getUserFromLocalStorage();
        requestStatus(false, null, null, userFromLocalStorage);
        snack("success", "Logged in successfully!");
        const from = intendedDestination(location, ROUTES.ROOT);
        navigate(from, { replace: true });
      } catch (error) {
        const detail =
          typeof error === "string"
            ? error
            : error?.message || GENERIC_LOGIN_FAILURE;
        const msg =
          isSupabaseConfigured() && supabase && mayBeUnmigratedAccount(detail)
            ? `${SUPABASE_LOGIN_FAILURE_HINT} (${detail})`
            : detail;
        requestStatus(false, msg, null);
        snack("error", msg);
      } finally {
        setLoading(false);
      }
    },
    [navigate, location, requestStatus, setToken, snack]
  );

  const handleLogout = useCallback(async () => {
    // B-59: the drafts a screen kept (a chat reply, an offer, a listing, the
    // last screen) are the user's own words; they leave with the user. A
    // session that merely expires keeps them, which is the point of keeping them.
    clearDrafts();
    removeToken();
    setToken(null);
    setUser(null);
    if (isSupabaseConfigured() && supabase) {
      await supabase.auth.signOut();
    }
  }, [setUser, setToken]);

  const handleGoogleLogin = useCallback(async () => {
    if (!isSupabaseConfigured() || !supabase) return;
    try {
      // [#77]. The destination RequireAuth saved lives in the router's
      // in-memory history entry, and the full-page hop to Google destroys it —
      // which is why this button used to land everyone on the map while the
      // password button beside it honoured the deep link. Hand it to storage
      // that survives the round trip BEFORE giving up control. `null` when
      // there is nothing saved, which clears any stale entry rather than
      // letting an abandoned attempt steer this one.
      //
      // Deliberately above [#174]'s trace reset, and that does not weaken it:
      // this writes a different sessionStorage key ("tradeloft.intendedDestination"
      // vs AUTH_TRACE_STORAGE_KEY), so `startGoogleOAuthTrace` cannot clobber it
      // and it cannot pollute the trace record.
      //
      // An earlier draft also claimed going first "means the destination
      // survives a throw from the trace block". Literally true, operationally
      // worthless, and struck rather than left standing: every statement in
      // that block precedes `signInWithOAuth`, so a throw is caught below,
      // snacks "Google sign-in could not start", and the redirect never
      // happens — nothing ever reads the surviving value.
      saveIntendedDestination(intendedDestination(location, null));
      // Reset FIRST. `startGoogleOAuthTrace` writes a whole fresh record with
      // `steps: []`, which is what starting a new flow means — but it used to
      // run after the two steps and the probe below, so it threw them away and
      // the callback's debug panel opened on a timeline that began at
      // `signInWithOAuthCalled` ([#174]).
      startGoogleOAuthTrace(ROUTES.AUTH_CALLBACK, isSupabaseConfigured());
      appendAuthStep("oauthStartClicked");
      const storageProbe = probeAuthStorage();
      mergeAuthTrace({ storageProbe });
      appendAuthStep("storageProbed", {
        localStorageWritable: storageProbe.localStorageWritable,
        sessionStorageWritable: storageProbe.sessionStorageWritable,
        pkceStorageKeyPresent: storageProbe.pkceStorageKeyPresent,
        pkceStorageKeyCount: storageProbe.pkceStorageKeyCount,
      });
      appendAuthStep("signInWithOAuthCalled");
      // On native, suppress supabase-js's own redirect. Letting it navigate the
      // WebView to Google would tear down the running React app; the user would
      // return to a cold boot. Instead take the URL and open it in a system
      // Custom Tab, leaving the app (and the PKCE verifier it just wrote to
      // storage) alive to receive the deep link back.
      const native = isNativePlatform();
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: authCallbackUrl, skipBrowserRedirect: native },
      });
      if (error) {
        appendAuthStep("signInWithOAuthFailed", {
          error: sanitizeExchangeError(error),
        });
        throw error.message;
      }
      if (native) {
        if (!data?.url) {
          appendAuthStep("signInWithOAuthMissingUrl");
          throw "Google sign-in could not start";
        }
        appendAuthStep("nativeAuthBrowserOpened");
        await openExternalAuthUrl(data.url);
      }
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message || "Google sign-in could not start";
      snack("error", msg);
    }
  }, [authCallbackUrl, location, snack]);

  const handleSignup = useCallback(
    async (user) => {
      try {
        setLoading(true);
        const normalizedUser = normalizeUser(user);
        if (isSupabaseConfigured() && supabase) {
          // Where sign-in was taking them (a shared trade list's offer, for
          // one). Saved before signUp so the email-confirmation link, which
          // lands on AuthCallbackPage in this browser, can still take them
          // there; read back below when the session exists at once. `null`
          // clears a stale entry, as in handleGoogleLogin.
          saveIntendedDestination(intendedDestination(location, null));
          const { data, error } = await supabase.auth.signUp({
            email: normalizedUser.email,
            password: user.password,
            options: {
              emailRedirectTo: authCallbackUrl,
              data: {
                first_name: normalizedUser.name.first,
                last_name: normalizedUser.name.last,
                // Carried in provider metadata so the bridge can enforce 18+
                // even when the session is established later, from the email
                // confirmation link, rather than immediately here.
                date_of_birth: normalizedUser.dateOfBirth,
                // #65. Same reason: the bridge records Terms/Privacy acceptance
                // on the account it creates, even when that happens later from
                // the email confirmation link. Only set because the signup form
                // blocked submit until the box was checked.
                accepted_terms: true,
                terms_accepted_at: new Date().toISOString(),
                // The version of the documents the form linked to (C1 of the
                // Q-20 review). The bridge records the acceptance against it
                // only if it is the server's current version; with none, it
                // records the older 2026-09-26 and the app asks again.
                policy_version: normalizedUser.policyVersion,
              },
            },
          });
          if (error) throw error.message;
          // #50. Stays `true`: signUp has returned but the flow has not, and
          // the page is still mounted with a live Submit under the user's
          // thumb for the whole bridge + navigate below. Dropping the busy
          // flag here re-armed it mid-flow, so a second tap fired a second
          // signUp even with Submit correctly gated on this flag. The
          // `finally` clears it on every exit, and #214's global
          // `axios.defaults.timeout` bounds the bridge, so the control can
          // never be left dead. Only the error/user fields are reset here.
          requestStatus(true, null, null);
          if (data.session) {
            const appToken = await syncSupabaseToApp(
              data.session.access_token,
              normalizedUser.dateOfBirth
            );
            setTokenInLocalStorage(appToken);
            setToken(appToken);
            // Fire here (not the email-confirm branch): analytics needs an auth
            // token, which only exists once a session is established.
            trackEvent("signup_complete", { method: "supabase" });
            const userFromLocalStorage = getUserFromLocalStorage();
            requestStatus(false, null, null, userFromLocalStorage);
            snack("success", "Logged in successfully!");
            navigate(takeIntendedDestination() || ROUTES.MAP, { replace: true });
          } else {
            snack(
              "success",
              "Check your email to confirm your account, then sign in."
            );
            // Signing in after confirming, in this tab, still goes on to the
            // destination.
            if (location.state?.from) {
              navigate(ROUTES.LOGIN, { state: { from: location.state.from } });
            } else {
              navigate(ROUTES.LOGIN);
            }
          }
          return;
        }
        const registration = await signup(normalizedUser);
        requestStatus(false, null, null);
        // The server now reports whether the verification email actually went
        // out (`emailSent`). Hardcoding "check your email" here would re-tell,
        // one layer up, the exact lie the server stopped telling.
        snack(
          registration?.emailSent === false ? "warning" : "success",
          registration?.message ||
            "Registration successful! Please check your email to verify your account."
        );
        navigate(ROUTES.LOGIN);
      } catch (error) {
        const msg =
          typeof error === "string"
            ? error
            : error?.message || "Signup failed — please try again";
        requestStatus(false, msg, null);
        snack("error", msg);
      } finally {
        setLoading(false);
      }
    },
    [authCallbackUrl, requestStatus, snack, navigate, setToken, location]
  );
  const handleGetUser = useCallback(
    async (userId) => {
      try {
        setLoading(true);
        const userFromClient = await GetUser(userId);
        if (userFromClient) {
          requestStatus(false, null, null, userFromClient);
          return userFromClient;
        }
        requestStatus(false, null, null);
      } catch (error) {
        const msg =
          typeof error === "string" ? error : error?.message || "Failed to get user";
        requestStatus(false, msg, null);
      }
    },
    [requestStatus]
  );
  const handleGetAllUsers = useCallback(
    async (userId, searchTerm = "") => {
      try {
        const allUsers = await GetAllUsers(userId, searchTerm);
        if (allUsers) setAllUsers(allUsers);
      } catch (error) {
        const msg =
          typeof error === "string" ? error : error?.message || "Failed to fetch users";
        requestStatus(false, msg, null);
      }
    },
    [requestStatus]
  );

  const handleDeleteAccount = useCallback(async () => {
    try {
      setLoading(true);
      const deletion = await deleteMyAccount();
      clearDrafts();
      removeToken();
      setToken(null);
      setUser(null);
      if (isSupabaseConfigured() && supabase) {
        await supabase.auth.signOut();
      }
      // The server answers a partial deletion with 207 + `status: "partial"`,
      // which axios treats as success. Hardcoding the short sentence here
      // announced a partial deletion in the same six words as a complete one —
      // and since the session has already been torn down above and the next
      // line navigates to LOGIN, that is the user's one and only chance to be
      // told. The discriminator is `status`, not `pendingStorageObjects`: the
      // server also sends "partial" for an unremovable auth identity, where
      // the storage count is 0. Same rule as the signup path above: report
      // what the server actually said.
      snack(
        deletion?.status === "partial" ? "warning" : "success",
        deletion?.message || "Your account has been deleted."
      );
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (error) {
      const msg =
        typeof error === "string" ? error : error?.message || "Failed to delete account";
      snack("error", msg);
    } finally {
      setLoading(false);
    }
  }, [navigate, setToken, setUser, snack]);

  const value = useMemo(() => {
    return { isLoading, error, user, allUsers, filteredUsers };
  }, [isLoading, error, user, allUsers, filteredUsers]);

  return {
    value,
    handleLogin,
    handleLogout,
    handleGoogleLogin,
    handleSignup,
    handleGetUser,
    handleGetAllUsers,
    handleDeleteAccount,
    setAllUsers,
  };
};

export default useHandleUsers;
